/**
 * Product Page Scripts
 * Handles variant selection, gallery sync, tabs, quantity selector,
 * read-more toggle, carousel, and mobile sticky ATC.
 */

document.addEventListener('DOMContentLoaded', () => {
    sortOptionPills();
    sortQtyPricingTable();
    initQuantitySelector();
    initVariantSelector();
    initProductGallery();
    initProductTabs();
    initReadMore();
    initCarousel();
    initMobileStickyATC();
});

// Re-run after all assets loaded so scrollHeight is accurate
window.addEventListener('load', initReadMore);

/* -------------------------------------------------------
   Sorting helpers
   ------------------------------------------------------- */

/**
 * Sort option pill buttons numerically (e.g. 1, 3, 6 instead of 6, 1, 3).
 * Extracts the leading number from each label's text content.
 */
function sortOptionPills() {
    document.querySelectorAll('.product-option__values').forEach(container => {
        const items = [];

        container.querySelectorAll('.product-option__input').forEach(input => {
            const label = input.nextElementSibling;
            if (!label) return;
            const num = parseInt(label.textContent.trim().match(/\d+/)?.[0]) || 0;
            items.push({ input, label, num });
        });

        items.sort((a, b) => a.num - b.num);
        items.forEach(item => {
            container.appendChild(item.input);
            container.appendChild(item.label);
        });
    });
}

/**
 * Sort qty-pricing table rows by quantity ascending (1, 3, 6, 12).
 */
function sortQtyPricingTable() {
    const table = document.getElementById('QtyPricingTable');
    if (!table) return;

    const rows = Array.from(table.querySelectorAll('.qty-pricing__row'));
    rows.sort((a, b) => {
        return (parseInt(a.dataset.qty) || 0) - (parseInt(b.dataset.qty) || 0);
    });
    rows.forEach(row => table.appendChild(row));
}

/* -------------------------------------------------------
   Quantity selector
   ------------------------------------------------------- */

function initQuantitySelector() {
    window.adjustProductQty = (amount) => {
        const input = document.getElementById('ProductQty');
        if (!input) return;

        const newVal = Math.max(1, (parseInt(input.value) || 1) + amount);
        input.value = newVal;
    };
}

/* -------------------------------------------------------
   Variant selector
   ------------------------------------------------------- */

function initVariantSelector() {
    const variantDataTag = document.getElementById('ProductVariantsJson');
    if (!variantDataTag) return;

    let variantData;
    try {
        variantData = JSON.parse(variantDataTag.textContent);
    } catch (e) {
        return;
    }

    const optionContainers = document.querySelectorAll('.product-option');
    const hiddenInput = document.getElementById('ProductVariantId');
    const priceDisplay = document.querySelector('.price-regular');
    const addToCartBtn = document.querySelector('.product-form__submit');
    const qtyRows = document.querySelectorAll('.qty-pricing__row');
    const themeConfig = window.__theme || {};
    const strings = themeConfig.strings || {};

    if (optionContainers.length === 0) return;

    const getSelectedOptions = () => {
        return Array.from(optionContainers).map(container => {
            const checked = container.querySelector('.product-option__input:checked');
            return checked ? checked.value : null;
        });
    };

    const findVariant = (options) => {
        return variantData.find(v => v.options.every((opt, i) => opt === options[i]));
    };

    const updateLinkedOptions = () => {
        const selectedOptions = getSelectedOptions();

        optionContainers.forEach((container, index) => {
            const testOptions = [...selectedOptions];

            container.querySelectorAll('.product-option__input').forEach(input => {
                testOptions[index] = input.value;
                input.disabled = !findVariant(testOptions);
            });
        });
    };

    const formatPrice = (cents) => {
        return new Intl.NumberFormat(themeConfig.locale || 'en', {
            style: 'currency',
            currency: themeConfig.currency || 'EUR',
        }).format(cents / 100);
    };

    const updateUI = () => {
        const variant = findVariant(getSelectedOptions());

        if (!variant) {
            if (addToCartBtn) {
                addToCartBtn.disabled = true;
                addToCartBtn.textContent = strings.unavailable || 'Unavailable';
            }
            return;
        }

        // Hidden input + URL
        if (hiddenInput) hiddenInput.value = variant.id;
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        // Price
        if (priceDisplay) {
            priceDisplay.textContent = formatPrice(variant.price);
        }

        // Add to Cart button
        if (addToCartBtn) {
            addToCartBtn.disabled = !variant.available;
            addToCartBtn.textContent = variant.available
                ? (strings.addToCart || 'Add to cart')
                : (strings.soldOut || 'Sold out');
        }

        // Qty-pricing table highlight
        const variantIdStr = String(variant.id);
        qtyRows.forEach(row => {
            row.classList.toggle('qty-pricing__row--active', row.dataset.variantId === variantIdStr);
        });
    };

    // Update per-pill prices based on current selection
    // For each option value, find the variant that matches (current selection with this value swapped in)
    // and show its price. Falls back to any variant with this value if no exact match.
    const updateOptionPrices = () => {
        const selectedOptions = getSelectedOptions();

        optionContainers.forEach((container, index) => {
            container.querySelectorAll('.product-option__input').forEach(input => {
                const testOptions = [...selectedOptions];
                testOptions[index] = input.value;

                let matchVariant = findVariant(testOptions);
                if (!matchVariant) {
                    // Fallback: any variant that has this value at this position
                    matchVariant = variantData.find(v => v.options[index] === input.value);
                }

                const pillLabel = container.querySelector(`label[for="${input.id}"]`);
                if (!pillLabel) return;
                const priceSpan = pillLabel.querySelector('[data-pill-price]');
                if (!priceSpan) return;

                priceSpan.textContent = matchVariant ? formatPrice(matchVariant.price) : '';
            });
        });
    };

    const updateColorLabels = () => {
        document.querySelectorAll('[data-color-option]').forEach(input => {
            if (!input.checked) return;
            const label = document.getElementById('SelectedColor-' + input.dataset.colorOption);
            if (label) label.textContent = input.value;
        });
    };

    // Listen for option changes
    optionContainers.forEach(container => {
        container.addEventListener('change', () => {
            updateLinkedOptions();
            updateUI();
            updateOptionPrices();
            updateColorLabels();
        });
    });

    // Initial state
    updateLinkedOptions();
    updateUI();
    updateOptionPrices();
}

/* -------------------------------------------------------
   Product gallery (thumbnail sync)
   ------------------------------------------------------- */

function initProductGallery() {
    const galleryMain = document.getElementById('ProductGalleryMain');
    const thumbnails = document.querySelectorAll('.product-gallery__thumbnail');

    if (!galleryMain || thumbnails.length === 0) return;

    // Thumbnail click -> scroll to slide
    thumbnails.forEach(thumb => {
        thumb.addEventListener('click', (e) => {
            e.preventDefault();
            const targetSlide = document.getElementById('Slide-' + thumb.dataset.index);
            if (targetSlide) {
                galleryMain.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
            }
        });
    });

    // Scroll observer -> update active thumbnail
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            const index = entry.target.id.replace('Slide-', '');
            thumbnails.forEach(t => t.classList.remove('active'));
            const active = document.querySelector('.product-gallery__thumbnail[data-index="' + index + '"]');
            if (active) active.classList.add('active');
        });
    }, { root: galleryMain, threshold: 0.5 });

    document.querySelectorAll('.product-gallery__slide').forEach(slide => observer.observe(slide));
}

/* -------------------------------------------------------
   Product tabs
   ------------------------------------------------------- */

function initProductTabs() {
    const tabBtns = document.querySelectorAll('.product-tabs__btn');
    const tabPanels = document.querySelectorAll('.product-tabs__panel');

    if (tabBtns.length === 0) return;

    tabBtns.forEach(btn => {
        btn.addEventListener('click', function () {
            const targetTab = this.dataset.tab;

            tabBtns.forEach(b => b.classList.remove('active'));
            this.classList.add('active');

            tabPanels.forEach(panel => {
                panel.classList.toggle('active', panel.id === 'tab-' + targetTab);
            });
        });
    });
}

/* -------------------------------------------------------
   Read more / less toggle for description
   ------------------------------------------------------- */

function initReadMore() {
    const wrapper = document.getElementById('descriptionWrapper');
    const content = document.getElementById('descriptionContent');
    const btn = document.getElementById('readMoreBtn');

    if (!wrapper || !content || !btn) return;
    if (btn.dataset.initialized === 'true') return;
    btn.dataset.initialized = 'true';

    const MAX_HEIGHT = 150;

    const checkHeight = () => {
        const needsCollapse = content.scrollHeight > MAX_HEIGHT;
        wrapper.classList.toggle('collapsed', needsCollapse);
        wrapper.style.maxHeight = needsCollapse ? MAX_HEIGHT + 'px' : 'none';
        btn.style.display = needsCollapse ? 'block' : 'none';
    };

    checkHeight();
    window.addEventListener('resize', checkHeight);

    btn.addEventListener('click', function () {
        const isCollapsed = wrapper.classList.contains('collapsed');
        wrapper.classList.toggle('collapsed');
        wrapper.style.maxHeight = isCollapsed ? content.scrollHeight + 'px' : MAX_HEIGHT + 'px';
        this.textContent = isCollapsed
            ? (this.dataset.readLess || 'Read less')
            : (this.dataset.readMore || 'Read more');
    });
}

/* -------------------------------------------------------
   Related products carousel
   ------------------------------------------------------- */

function initCarousel() {
    const SCROLL_AMOUNT = 300;

    document.querySelectorAll('[data-carousel]').forEach(carousel => {
        const track = carousel.querySelector('[data-carousel-track]');
        const prevBtn = carousel.querySelector('[data-carousel-prev]');
        const nextBtn = carousel.querySelector('[data-carousel-next]');

        if (!track) return;

        const updateButtons = () => {
            if (prevBtn) prevBtn.disabled = track.scrollLeft <= 0;
            if (nextBtn) nextBtn.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 10;
        };

        if (prevBtn) prevBtn.addEventListener('click', () => track.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' }));
        if (nextBtn) nextBtn.addEventListener('click', () => track.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' }));

        track.addEventListener('scroll', updateButtons);
        updateButtons();
    });
}

/* -------------------------------------------------------
   Mobile sticky Add to Cart bar
   ------------------------------------------------------- */

function initMobileStickyATC() {
    const stickyBar = document.getElementById('MobileStickyATC');
    const stickyBtn = document.getElementById('MobileStickyATCBtn');
    const mainSubmit = document.querySelector('.product-form__submit');

    if (!stickyBar || !stickyBtn || !mainSubmit) return;

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        });
    }, { threshold: 0 });

    observer.observe(mainSubmit);

    stickyBtn.addEventListener('click', () => mainSubmit.click());
}
